/* ════════════ THE REDACTION ════════════
   The snowden page's atmosphere: program codewords surface out of
   the dark, hold for a breath, then a redaction bar sweeps across
   and the record is gone — declassification, run in reverse. */

import { pixelatePortraits } from "./pixelate";
import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function redact(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#redact")!;
  const ctx = c.getContext("2d")!;

  const WORDS = [
    "PRISM", "XKEYSCORE", "TEMPORA", "MUSCULAR", "STELLARWIND",
    "BULLRUN", "DISHFIRE", "OPTIC NERVE", "CO-TRAVELER", "BOUNDLESS",
    "TS//SI//NOFORN", "FISA", "SECTION 215", "FIVE EYES", "UPSTREAM",
    "metadata", "collect it all", "not wittingly",
  ];
  const CYAN = "#2bd9ff";
  const GREY = "#9aa0a6";

  type Rec = {
    x: number;
    y: number;
    t: number; // age in frames
    life: number;
    size: number;
    col: string;
    word: string;
    w: number; // measured text width
  };
  let recs: Rec[] = [];

  const spawn = (): Rec => {
    const isCyan = Math.random() < 0.15;
    const word = WORDS[(Math.random() * WORDS.length) | 0]!;
    const size = isCyan ? 13 + Math.random() * 6 : 10 + Math.random() * 4;
    ctx.font = `${size}px "IBM Plex Mono", monospace`;
    return {
      x: Math.random() * c.width,
      y: Math.random() * c.height,
      t: 0,
      life: 200 + Math.random() * 260,
      size,
      col: isCyan ? CYAN : GREY,
      word,
      w: ctx.measureText(word).width,
    };
  };

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    const count = Math.min(48, Math.floor((c.width * c.height) / 38000));
    recs = Array.from({ length: count }, () => {
      const r = spawn();
      r.t = Math.random() * r.life; // desync
      return r;
    });
  };
  seed();
  addEventListener("resize", seed, { signal });

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    for (const r of recs) {
      const p = r.t / r.life;
      // fade in, hold readable, then the record dissolves under the bar
      const a = p < 0.12 ? p / 0.12 : p > 0.78 ? (1 - p) / 0.22 : 1;
      ctx.globalAlpha = a * (r.col === CYAN ? 0.5 : 0.22);
      ctx.fillStyle = r.col;
      ctx.font = `${r.size}px "IBM Plex Mono", monospace`;
      ctx.fillText(r.word, r.x, r.y);
      // redaction sweep: from 40% of life the bar crosses left-to-right
      if (p > 0.4) {
        const sweep = Math.min(1, (p - 0.4) / 0.35);
        ctx.globalAlpha = a * 0.85;
        ctx.fillStyle = "#03070c";
        ctx.fillRect(r.x - 2, r.y - r.size + 2, (r.w + 4) * sweep, r.size + 4);
        ctx.globalAlpha = a * (r.col === CYAN ? 0.45 : 0.2);
        ctx.fillStyle = r.col;
        ctx.fillRect(r.x - 2, r.y - r.size + 2, (r.w + 4) * sweep, 1);
        ctx.fillRect(r.x - 2, r.y + 5, (r.w + 4) * sweep, 1);
      }
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    for (let i = 0; i < recs.length; i++) {
      const r = recs[i]!;
      r.t++;
      r.y -= 0.06; // drifting up, files leaving the building
      if (r.t >= r.life) recs[i] = spawn();
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

onPage((signal) => {
  redact(signal);
  pixelatePortraits();
});
